import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { of } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import { CachingService } from './caching.service';
import { CallAPIService } from './call-api.service';
import { cacheKeys } from '../constants/cache-keys';
import { APIConfig } from '../models/api-config.model';
import { Service } from '../models/all-services.model';
import { Banner } from '../models/banner.model';
import { AllStats } from '../models/statistics.model';
import { Category } from '../models/categories-model';
import { WhatsNewService } from '../models/whats-new-services.model';
import { PopularService } from '../models/popular-services.model';
import { TrendingService } from '../models/trending-services.model';
import { isNil } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class APIConfigService {
  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private cachingService: CachingService,
    private callApiService: CallAPIService
  ) {}

  private getServicesConfig(): APIConfig {
    const cachedServices = this.cachingService.fetchCachedData<Service[]>(
      cacheKeys.allServices
    );
    const getServicesObs$ = cachedServices?.length
      ? of(cachedServices)
      : this.callApiService.fetchAllServicesFromAPI();

    return {
      key: cacheKeys.allServices,
      getDataObs$: getServicesObs$,
    };
  }

  private getBannersConfig(): APIConfig {
    const cachedBanners = this.cachingService.fetchCachedData<Banner[]>(
      cacheKeys.banners
    );

    // isPlatformServer(this.platformId) ? of([]) : cachedBanners?.length;

    const getBannersObs$ = cachedBanners?.length
      ? of(cachedBanners)
      : this.callApiService.fetchBannerImages();

    return {
      getDataObs$: getBannersObs$,
      key: cacheKeys.banners,
    };
  }

  public getStatisticsConfig(): APIConfig {
    const cachedStats = this.cachingService.fetchCachedData<AllStats>(
      cacheKeys.statistics
    );
    if(isPlatformServer(this.platformId)){
      return {
        getDataObs$:of({}),
        key: cacheKeys.statistics,
      };
    }else{
      const getStatsObs$ = cachedStats
      ? of(cachedStats)
      : this.callApiService.fetchStatsRecord();
    return {
      getDataObs$: getStatsObs$,
      key: cacheKeys.statistics,
    };
    }

  }

  private getWhatsNewSrvsConfig(): APIConfig {
    const cachedWhatsNewSrvs = this.cachingService.fetchCachedData<
      WhatsNewService[]
    >(cacheKeys.whatsNewSrvs);
    const getWhatsNewSrvsObs$ = cachedWhatsNewSrvs?.length
      ? of(cachedWhatsNewSrvs)
      : this.callApiService.fetchWhatsNewSrvs();

    return {
      getDataObs$: getWhatsNewSrvsObs$,
      key: cacheKeys.whatsNewSrvs,
    };
  }

  private getPopularSrvsConfig(): APIConfig {
    const cachedPopularSrvs = this.cachingService.fetchCachedData<
      PopularService[]
    >(cacheKeys.popularSrvs);
    const getPopularSrvsObs$ = cachedPopularSrvs?.length
      ? of(cachedPopularSrvs)
      : this.callApiService.fetchPopularServices();

    return {
      getDataObs$: getPopularSrvsObs$,
      key: cacheKeys.popularSrvs,
    };
  }

  private getTrendingSrvsConfig(): APIConfig {
    const cachedTrendingSrvs = this.cachingService.fetchCachedData<
      TrendingService[]
    >(cacheKeys.trendingSrvs);
    const getTrendingSrvsObs$ = cachedTrendingSrvs?.length
      ? of(cachedTrendingSrvs)
      : this.callApiService.fetchTrendingServices();

    return {
      getDataObs$: getTrendingSrvsObs$,
      key: cacheKeys.trendingSrvs,
    };
  }

  private getCategoriesConfig(): APIConfig {
    const cachedCategories = this.cachingService.fetchCachedData<Category[]>(
      cacheKeys.categories
    );
    const getCategoriesObs$ = cachedCategories?.length
      ? of(cachedCategories)
      : this.callApiService.fetchCategories();

    return {
      getDataObs$: getCategoriesObs$,
      key: cacheKeys.categories,
    };
  }

  /**
   * Method used to manage states of APIs in HOME page.
   * @returns Array of configurations for HOME page.
   */
  getHomePageConfig() {
    return [
      this.getBannersConfig(),
      this.getStatisticsConfig(),
      this.getWhatsNewSrvsConfig(),
      this.getPopularSrvsConfig(),
      this.getTrendingSrvsConfig(),
      this.getCategoriesConfig(),
    ];
  }

  /**
   * Method used to manage states of APIs in ALL_SERVICES page.
   * @returns Array of configurations for ALL_SERVICES page.
   */
  getAllServicesPageConfig() {
    return [this.getCategoriesConfig(), this.getServicesConfig()];
  }

  /**
   * Method used to manage states of APIs in DEPARTMENT_DETAIL page.
   * @returns Array of configurations for DEPARTMENT_DETAIL page.
   */
  getDeptDetailPageConfig() {
    return [this.getServicesConfig()];
  }

  getGlobalSearchConfig() {
    return [this.getTrendingSrvsConfig()];
  }

  getSchemeApplyConfig(): APIConfig {
    const cachedSchemeApplyData = this.cachingService.fetchCachedData<any>(
      cacheKeys.schemeApplyData
    );
    const getSchemeDataObs$ = !isNil(cachedSchemeApplyData)
      ? of(cachedSchemeApplyData)
      : this.callApiService.fetchSchemeData();
    return {
      getDataObs$: getSchemeDataObs$,
      key: cacheKeys.schemeApplyData,
    };
  }
}
